import React, { useEffect, useMemo, useState } from 'react'
import useApiData from '../../services/useApiData.js'
import { IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close'
import { Autocomplete, TextField, Button } from '@mui/material';
import PostData from '../../services/PostData.js';
import PutData from '../../services/PutData.js';
import PostPrintLabel from '../../services/PostPrintLabel.js';
import { openPdfInNewTab } from '../../services/openPrintTab.js';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import { CircularProgress } from '@mui/material';

const LIST_PLATFORMS = ["eBay", "Walmart", "Shopify"];
const DEFAULT_STATUS = "Inspection";
const STATUSES = [
    "Inspection",
    "Putaway",
    "Failed Inspection",
    "Awaiting Pickup",
    "Received",
    "Listed",
    "Allocated",
    "Picked",
    "Missing",
    "Shipped",
    "Pending eBay",
    "Pending Walmart",
    "Reserves / Refurbish",
    "Investigation Pending",
    "Bundled",
    "Scrapped",
];

export default function OpenLabelRecord({ recordId, closeDrawer }) {
    const itemId = recordId?.id || recordId?.row?.id || "";
    const { data: item, load: loadingItem } = useApiData(
        itemId ? `api/broylesllc/catalog-items/${itemId}` : null,
        { enabled: Boolean(itemId) }
    );
    const { data: shelfList, load: fetchingShelf } = useApiData("api/broylesllc/shelf-options");

    const [skuQuery, setSkuQuery] = useState("");
    const [debouncedSku, setDebouncedSku] = useState("");
    const [submitLoad, setSubmitLoad] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [chosenOptions, setChosenOptions] = useState({
        listPlatform: "",
        status: "",
        shelf: null,
        shelfBinId: "",
        product: null,
        expireDate: null,
    });

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSku(skuQuery.trim()), 300);
        return () => clearTimeout(timer);
    }, [skuQuery]);

    const productUrl = debouncedSku
        ? `api/broylesllc/products?q=${encodeURIComponent(debouncedSku)}&pageSize=20`
        : "";
    const { data: productData, load: fetchingSkus } = useApiData(
        productUrl || "api/broylesllc/products?pageSize=1",
        { enabled: Boolean(productUrl) }
    );

    useEffect(() => {
        if (!item?._id) return;
        const listPlatform = (item.listPlatform || []).find((value) =>
            LIST_PLATFORMS.includes(value)
        ) || "";
        const shelfId = item.shelfIds?.[0];
        const shelfName = item.shelves?.[0] || "";
        setChosenOptions((prev) => ({
            listPlatform,
            // Needs Label completion advances to Inspection; user can override.
            status: DEFAULT_STATUS,
            // Keep a previously applied default shelf when the item has none.
            shelf: shelfId ? { id: shelfId, shelfName } : prev.shelf,
            shelfBinId: item.shelfBinId ? String(item.shelfBinId) : prev.shelfBinId || "",
            product: item.product?._id
                ? {
                    _id: item.product._id,
                    listingField: item.product.listingField || item.product.itemName || "",
                }
                : null,
            expireDate: item.expirationDate ? new Date(item.expirationDate) : null,
        }));
    }, [item]);

    useEffect(() => {
        const list = Array.isArray(shelfList) ? shelfList : [];
        if (!list.length) return;
        const defaultShelf = list.find((shelf) => {
            const name = shelf?.shelfName || "";
            return (
                name === "B06 Pending Shelf - 06/18" ||
                name === "B06 Pending Shelf" ||
                name.startsWith("B06 Pending Shelf")
            );
        });
        if (!defaultShelf) return;
        setChosenOptions((prev) => {
            if (prev.shelf) return prev;
            return { ...prev, shelf: defaultShelf };
        });
    }, [shelfList]);

    const shelfOptions = useMemo(() => {
        const list = Array.isArray(shelfList) ? shelfList : [];
        const current = chosenOptions.shelf;
        if (current?.id && !list.some((option) => option.id === current.id)) {
            return [current, ...list];
        }
        return list;
    }, [shelfList, chosenOptions.shelf]);

    const shelfBins = useMemo(() => {
        const selected = shelfOptions.find(
            (option) => option.id === chosenOptions.shelf?.id
        );
        return Array.isArray(selected?.bins) ? selected.bins : [];
    }, [shelfOptions, chosenOptions.shelf]);

    const skuOptions = useMemo(() => {
        const found = productData?.items || [];
        const current = chosenOptions.product;
        if (current?._id && !found.some((option) => String(option._id) === String(current._id))) {
            return [current, ...found];
        }
        return found;
    }, [productData, chosenOptions.product]);

    const handleClose = () => {
        closeDrawer();
    };

    const handlePlatformSelect = (_event, newValue) => {
        setChosenOptions((prev) => ({
            ...prev,
            listPlatform: newValue || "",
            status: prev.status || DEFAULT_STATUS,
        }));
    };

    const handleSubmit = async () => {
        const itemNum = String(item?.parserItemId || item?.itemId || "").trim();
        if (!itemNum) {
            setErrorMessage("A record id is required to print a label.");
            return;
        }
        setErrorMessage("");
        setSubmitLoad(true);
        const payload = {
            status: chosenOptions.status || DEFAULT_STATUS,
            shelfId: chosenOptions.shelf?.id || null,
            shelfBinId: chosenOptions.shelf?.id
                ? chosenOptions.shelfBinId || null
                : null,
        };
        if (chosenOptions.listPlatform) {
            payload.listPlatform = [chosenOptions.listPlatform];
        }
        if (chosenOptions.product?._id) {
            payload.product = chosenOptions.product._id;
        }
        if (chosenOptions.expireDate instanceof Date && !Number.isNaN(chosenOptions.expireDate.getTime())) {
            payload.expirationDate = chosenOptions.expireDate.toISOString();
        }

        try {
            const saved = await PutData(payload, `broylesllc/catalog-items/${itemId}`);
            if (!saved.success) {
                setErrorMessage(saved.data?.message || "Could not update this catalog item.");
                return;
            }

            const printed = await PostPrintLabel(
                [{ id: itemId, itemNum }],
                "broylesllc/print-labels"
            );
            if (!printed.data) {
                setErrorMessage(printed.error?.message || "Error printing label.");
                return;
            }
            if (!openPdfInNewTab(printed.data)) {
                setErrorMessage("Allow popups for this site to print labels.");
                return;
            }

            const cleared = await PostData(
                { ids: [itemId] },
                "broylesllc/catalog-items/clear-needs-label",
                null
            );
            if (cleared.success) {
                closeDrawer("success");
            } else {
                setErrorMessage("Label printed, but the item is still marked as needing a label.");
            }
        } finally {
            setSubmitLoad(false);
        }
    };

    return (
        <div className='card'
            style={{
                width: "100%", height: '100%', border: 'none',
                display: 'flex', flexDirection: 'column'
            }}>
            <div className='card-header d-flex justify-content-between align-items-center'
                style={{ border: 'none', backgroundColor: 'white', borderBottom: 'solid lightgray', padding: '10px 15px' }}>
                <div>
                    <h2 style={{ margin: '5px 0 0 0' }}>Update + print record</h2>
                    <div style={{ color: '#627d98', fontSize: 13 }}>
                        {item?.parserItemId || item?.itemId || ""}
                    </div>
                </div>
                <IconButton
                    onClick={handleClose}
                    sx={{
                        width: '40px', height: '40px',
                        boxShadow: "rgba(0, 0, 0, 0.16) 0px 1px 4px",
                        backgroundColor: 'white', zIndex: 100, "&:hover": { backgroundColor: '#f0f0f0' }
                    }}
                >
                    <CloseIcon sx={{ cursor: 'pointer' }} />
                </IconButton>
            </div>
            <div className='card-body'
                style={{
                    marginTop: '10px', flexGrow: 1,
                    display: 'flex', flexDirection: 'column'
                }}>
                {loadingItem ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
                        <CircularProgress size={28} />
                    </div>
                ) : (
                    <>
                        <Autocomplete
                            value={chosenOptions.listPlatform || null}
                            disabled={submitLoad}
                            onChange={handlePlatformSelect}
                            options={LIST_PLATFORMS}
                            style={{ marginBottom: '16px' }}
                            renderInput={(params) => (
                                <TextField {...params} label="List Platform" variant="outlined" size='small' />
                            )}
                        />
                        <Autocomplete
                            value={chosenOptions.status || null}
                            disabled={submitLoad}
                            onChange={(_event, newValue) => {
                                setChosenOptions((prev) => ({
                                    ...prev,
                                    status: newValue || "",
                                }));
                            }}
                            options={STATUSES}
                            style={{ marginBottom: '16px' }}
                            renderInput={(params) => (
                                <TextField {...params} label="Status" variant="outlined" size='small' />
                            )}
                        />
                        <Autocomplete
                            loading={fetchingShelf}
                            disabled={submitLoad}
                            value={chosenOptions.shelf}
                            onChange={(_event, newValue) => {
                                setChosenOptions((prev) => ({
                                    ...prev,
                                    shelf: newValue || null,
                                    shelfBinId: "",
                                }));
                            }}
                            options={shelfOptions}
                            getOptionLabel={(option) => option?.shelfName || ""}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            style={{ marginBottom: '16px' }}
                            renderInput={(params) => (
                                <TextField {...params} label="Shelf Location" variant="outlined" size='small' />
                            )}
                        />
                        {shelfBins.length > 0 ? (
                            <Autocomplete
                                disabled={submitLoad}
                                value={shelfBins.find((bin) => bin.id === chosenOptions.shelfBinId) || null}
                                onChange={(_event, newValue) => {
                                    setChosenOptions((prev) => ({
                                        ...prev,
                                        shelfBinId: newValue?.id || "",
                                    }));
                                }}
                                options={shelfBins}
                                getOptionLabel={(option) => option?.name || ""}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                style={{ marginBottom: '16px' }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Bin"
                                        variant="outlined"
                                        size="small"
                                        helperText="Optional. Bins configured on this shelf."
                                    />
                                )}
                            />
                        ) : null}
                        <Autocomplete
                            loading={fetchingSkus}
                            disabled={submitLoad}
                            loadingText="Searching SKUs…"
                            noOptionsText={skuQuery.trim() ? "No SKUs found" : "Type to search SKUs"}
                            value={chosenOptions.product}
                            onChange={(_event, newValue) => {
                                setChosenOptions((prev) => ({
                                    ...prev,
                                    product: newValue,
                                }));
                            }}
                            onInputChange={(_event, value, reason) => {
                                if (reason === "input") setSkuQuery(value);
                            }}
                            options={skuOptions}
                            getOptionLabel={(option) =>
                                option?.listingField || option?.itemName || option?.internalSkuId || ""
                            }
                            isOptionEqualToValue={(option, value) =>
                                String(option?._id) === String(value?._id)
                            }
                            filterOptions={(options) => options}
                            style={{ marginBottom: '16px' }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="SKU Name"
                                    variant="outlined"
                                    size="small"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {fetchingSkus && (
                                                    <CircularProgress color="inherit" size={20} />
                                                )}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                                label="Expiration Date"
                                value={chosenOptions.expireDate}
                                disabled={submitLoad}
                                onChange={(newValue) => {
                                    setChosenOptions((prev) => ({
                                        ...prev,
                                        expireDate: newValue,
                                    }));
                                }}
                                slotProps={{
                                    textField: { fullWidth: true, size: 'small' },
                                }}
                            />
                        </LocalizationProvider>
                        {errorMessage ? (
                            <div style={{ color: '#b42318', marginTop: 12, fontSize: 14 }}>
                                {errorMessage}
                            </div>
                        ) : null}
                    </>
                )}
            </div>
            <div className='card-footer'
                style={{
                    padding: '16px', borderTop: '1px solid #e0e0e0',
                    backgroundColor: 'white'
                }}>
                <Button
                    disabled={submitLoad || loadingItem}
                    variant="contained"
                    sx={{
                        boxShadow: 'none', borderRadius: '5px',
                        backgroundColor: '#3E5C76',
                        '&:hover': { backgroundColor: "#3e5c76f0", boxShadow: 'none' }
                    }}
                    fullWidth
                    onClick={handleSubmit}
                >
                    {submitLoad ? "Printing…" : "Print Label + Update Record"}
                </Button>
            </div>
        </div>
    );
}
